import type { Gate } from '@/components/GateChecks';

/**
 * Whether this run would actually ship anything.
 *
 * The structure tree and the delivered tree are two different derivations and
 * they disagree. The structure route builds its tree from the sealed walk's
 * tools and strays - what the package *would* hold - while the delivery
 * manifest reads `packaging_record_dispositions` and takes CANONICAL rows
 * only. UNASSIGNED and DROPPED carry a reason and no path, which is curation
 * saying the object is not part of the package, so a delivery never ships
 * them.
 *
 * Registering the population is what turns walk facts into dispositions. Until
 * that has run, the tree can show tens of thousands of files against a
 * manifest of zero, and the executor refuses the delivery for an empty tree.
 * Drawing the projection as "what arrives" is the one lie this screen could
 * tell that a buyer would discover instead of an operator.
 *
 * The population criterion is the backend's own answer to this, so it is read
 * rather than recomputed, and its `detail` is quoted rather than paraphrased.
 */

export const POPULATION_CHECK = 'Population dispositioned';

export interface Shipping {
  /** False until the gate has been evaluated; unknown is not zero. */
  known: boolean;
  /** True only when the backend says the population is registered and dispositioned. */
  willShip: boolean;
  /** The backend's own sentence, e.g. "0 records registered, 0 without a current disposition". */
  detail?: string;
}

export function shippingState(gate: Gate | null | undefined): Shipping {
  if (!gate) return { known: false, willShip: false };
  const check = gate.checks.find((c) => c.check === POPULATION_CHECK);
  // A gate that ran without this criterion is not evidence that a population
  // exists; it is a gate this code does not understand, and claiming a package
  // ships on that basis is exactly the failure being prevented.
  if (!check) return { known: false, willShip: false };
  return { known: true, willShip: check.pass, detail: check.detail };
}

/**
 * Whether naming may proceed.
 *
 * Fail-closed on purpose. Naming leaves STRUCTURING for good and STRUCTURING
 * is the only state that accepts a population, so this is the one irreversible
 * control in the wizard. The first version read `known && !willShip`, which
 * left it enabled for the whole time the gate was in flight - seconds on a
 * large bucket, and exactly when someone is typing a name.
 *
 * An unknown package is not a permitted one.
 */
export function namingAllowed(ship: Shipping): boolean {
  return ship.willShip;
}

/**
 * How much of the population is registered, as a measure rather than a clock.
 *
 * The step showed a spinner and an elapsed timer, and the timer counts the
 * wrong thing: it says how long you have waited, never how much is left. An
 * operator watching "4m 41s" against "0 records registered" cannot tell a job
 * that is grinding through 42,247 objects from one that refused at the first.
 *
 * Both numbers are already in the gate, in two criteria that are read on the
 * same response - the population criterion carries what is registered, and the
 * conservation criterion carries the inventory it is registering against. They
 * are sentences rather than fields, so this parses them defensively and
 * returns null when either does not read, in which case the caller shows the
 * backend's own words unchanged rather than a number it had to guess.
 */
export interface PopulationProgress {
  registered: number;
  total: number;
  remaining: number;
  /** 0-100, for a bar. Never above 100 even if the two criteria disagree. */
  percent: number;
}

const COUNT = /([\d,]+)\s+records registered/i;
const INVENTORY = /inventory\s+([\d,]+)/i;
const num = (raw: string | undefined) => (raw ? Number(raw.replace(/,/g, '')) : NaN);

export function populationProgress(gate: Gate | null | undefined): PopulationProgress | null {
  if (!gate) return null;
  const registered = num(gate.checks.find((c) => c.check === POPULATION_CHECK)?.detail?.match(COUNT)?.[1]);
  // Any criterion that states the inventory will do; the conservation one does.
  const total = num(gate.checks.map((c) => c.detail?.match(INVENTORY)?.[1]).find(Boolean));
  if (!Number.isFinite(registered) || !Number.isFinite(total) || total <= 0) return null;
  const capped = Math.min(registered, total);
  return {
    registered,
    total,
    remaining: Math.max(0, total - capped),
    percent: Math.round((capped / total) * 100),
  };
}
